/* eslint-disable lines-between-class-members,@typescript-eslint/camelcase */

import { VueConstructor } from 'vue';
import { Vue } from 'vue-property-decorator';

import {
  AuthorizationServiceConfiguration,
  FetchRequestor,
  TokenRequestHandler,
  TokenRequest,
  TokenResponse,
  RevokeTokenRequest,
  GRANT_TYPE_AUTHORIZATION_CODE,
  GRANT_TYPE_REFRESH_TOKEN,
} from '@openid/appauth/built';

import { v4 as uuid } from 'uuid';

import CustomTokenRequestHandler from './handlers/token';

import { AuthResult, AuthState, OAuth as Interface, OAuthParams, TokenPayload } from './interface';
import OAuthStorage from './storage';
import mixin from './mixin';
import { warn } from './helpers';
import { Apps } from '@/constants/apps';

export default class OAuth implements Interface {
  static installed = false;

  public readonly authState: Record<Apps, AuthState> = {
    [Apps.Homescapes]: Vue.observable({
      accessToken: null,
      tokenPayload: null,
    }),
    [Apps.Township]: Vue.observable({
      accessToken: null,
      tokenPayload: null,
    }),
  };

  private readonly configuration: AuthorizationServiceConfiguration;
  private readonly tokenHandler: TokenRequestHandler;

  private readonly storage: OAuthStorage;

  private readonly clientIds: Record<Apps, number>;
  private readonly redirectUri: string;

  private state: Record<Apps, string | null> = {
    [Apps.Homescapes]: null,
    [Apps.Township]: null,
  };

  private tokenResponse: Record<Apps, TokenResponse | null> = {
    [Apps.Homescapes]: null,
    [Apps.Township]: null,
  };

  constructor({ clientIds, redirectUri, serviceConfiguration }: OAuthParams) {
    this.configuration = new AuthorizationServiceConfiguration(serviceConfiguration);
    this.tokenHandler = new CustomTokenRequestHandler(new FetchRequestor());

    this.storage = new OAuthStorage(localStorage);

    this.clientIds = clientIds;
    this.redirectUri = redirectUri;

    Object.values(Apps).forEach(appName => {
      this._restoreSavedState(appName);
    });
  }

  refreshState(appName: Apps | undefined): string {
    if (appName === undefined) {
      return '';
    }

    const state = uuid();

    this.state[appName] = state;

    this.storage.storeState(appName, state);

    return state;
  }

  async generateToken(appName: Apps, code: string, state: string): Promise<AuthResult> {
    if (!state || this.state[appName] !== state) {
      throw new Error('Invalid state');
    }

    this.storage.clearState(appName);

    const request = new TokenRequest({
      code,
      client_id: this.clientIds[appName].toString(),
      redirect_uri: this.redirectUri,
      grant_type: GRANT_TYPE_AUTHORIZATION_CODE,
    });

    return this.tokenHandler
      .performTokenRequest(this.configuration, request)
      .then(response => this._handleTokenResponse(appName, response));
  }

  async refreshToken(appName: Apps): Promise<AuthResult> {
    if (!this.tokenResponse) {
      throw new Error('Not authorized');
    }

    const request = new TokenRequest({
      client_id: this.clientIds[appName].toString(),
      redirect_uri: this.redirectUri,
      refresh_token: this.tokenResponse[appName]?.refreshToken,
      grant_type: GRANT_TYPE_REFRESH_TOKEN,
    });

    return this.tokenHandler
      .performTokenRequest(this.configuration, request)
      .then(response => this._handleTokenResponse(appName, response));
  }

  async revokeToken(appName: Apps): Promise<boolean> {
    const token = this.tokenResponse[appName];
    if (!token) {
      throw new Error('Not authorized');
    }

    const request = new RevokeTokenRequest({
      token: token.accessToken,
    });

    return this.tokenHandler
      .performRevokeTokenRequest(this.configuration, request)
      .finally(() => this.resetToken(appName));
  }

  checkAuthorized(appName: Apps): boolean {
    return Boolean(this.tokenResponse[appName]);
  }

  checkTokenValid(appName: Apps): boolean {
    const token = this.tokenResponse[appName];
    if (!token) {
      return false;
    }
    return token.isValid();
  }

  resetToken(appName: Apps): void {
    this._updateTokenResponse(appName, null);

    this.storage.clear(appName);
  }

  private _handleTokenResponse(appName: Apps, response: TokenResponse): AuthResult {
    return this._updateTokenResponse(appName, response) as AuthResult;
  }

  private _updateTokenResponse(appName: Apps, response: TokenResponse | null): AuthState {
    this.tokenResponse[appName] = response;

    if (response) {
      this.storage.storeResponse(appName, response);
    } else {
      this.storage.clearResponse(appName);
    }

    return this._updateAuthState(appName, response && response.accessToken);
  }

  private _restoreSavedState(appName: Apps) {
    this.state[appName] = this.storage.retrieveState(appName);

    const response = this.storage.retrieveResponse(appName);
    this._updateTokenResponse(appName, response);
  }

  private _updateAuthState(appName: Apps, accessToken: string | null): AuthState {
    const tokenPayload = accessToken ? this._parseTokenPayload(accessToken) : null;
    const authState = { accessToken, tokenPayload };

    Object.assign(this.authState[appName], authState);

    return authState;
  }

  private _parseTokenPayload(accessToken: string): TokenPayload {
    const [payload] = accessToken.split('.').slice(1);

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => {
          const char = c.charCodeAt(0).toString(16);
          const code = `00${char}`.slice(-2);

          return `%${code}`;
        })
        .join('')
    );

    return JSON.parse(json);
  }

  /*
   * Vue plugin install function
   */
  static install(Vue: VueConstructor): void {
    if (process.env.NODE_ENV !== 'production' && this.installed) {
      warn('Already installed');

      return;
    }

    this.installed = true;

    Vue.mixin(mixin);

    Object.defineProperties(Vue.prototype, {
      $oauth: {
        get() {
          return this._oauth;
        },
      },
    });

    const strategies = Vue.config.optionMergeStrategies;

    // eslint-disable-next-line func-names
    strategies.oauth = function(parentVal: never, childVal: never) {
      return childVal === undefined ? parentVal : childVal;
    };
  }
}
